import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { ActionCard } from './cards/action-feed-card';
import { AnimatePresence } from 'motion/react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ActionCardSkeleton } from './cards/action-card-skeleton';
import { cn } from '@shadcn/ui/lib/utils';
import { FeedHeader } from './feed-header';
import { useActionCardDetails } from '../hooks/use-action-card-details';
import { motion } from 'motion/react';
import { debounce } from '../../../../helpers/debounce';

import { MessageSquare, Filter, Settings } from 'lucide-react';
import { useSaltfish } from '../../../hooks/use-saltfish';
import { SimpleMessageFilters } from './simple-message-filters';
import { Button } from '@shadcn/ui/components/ui/button';
import { useSimpleContactFilters } from '../hooks/use-simple-contact-filters';
import { GET_CONTACTS_WEIGHTED_SCORES, MARK_ACTION_AS_NOT_INTERESTED } from '@zaplify/graphql';
import { useMutation } from '@apollo/client';
import { WeightKeys } from '@andsend/intelligence-shared';
import { WeightSliders } from './weight-sliders';

interface ActionFeedProps {}

export const ActionFeedV3 = ({}: ActionFeedProps) => {
    const [useAdvancedFilters, setUseAdvancedFilters] = useState(false);
    const [isFilterChanging, setIsFilterChanging] = useState(false);
    const [showWeightSliders, setShowWeightSliders] = useState(false);

    // Simple conversation filters
    const {
        activeFilter,
        contacts: simpleContacts,
        isLoading: simpleIsLoading,
        hasActiveFilter: simpleHasActiveFilter,
        totalCount: simpleTotalCount,
        updateFilter,
        updateCustomWeights,
        updateCustomTemporalWeights,
        currentWeights,
        currentTemporalWeights,
        customWeights,
        customTemporalWeights,
        loadMore,
        hasMoreData,
        isLoadingMore,
    } = useSimpleContactFilters({
        pageSize: 10,
    });

    const [markActionAsNotInterested] = useMutation(MARK_ACTION_AS_NOT_INTERESTED);

    // Use the appropriate filter results based on the current mode
    // const prospectIds = useMemo(() => simpleContacts.map((contact) => contact.prospectId), [simpleContacts]);
    const isLoading = simpleIsLoading;
    const hasActiveFilter = simpleHasActiveFilter;
    const totalCount = simpleTotalCount;

    const completedProspectIds: string[] = [];
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const feedContainerRef = useRef<HTMLDivElement>(null);
    const selectedProspectId = useParams().prospectId;
    const navigate = useNavigate();
    const { startSaltfishPlaylist } = useSaltfish();
    // const [snapshotProspectIds, setSnapshotProspectIds] = useState<string[]>([]);
    // const [newActionsProspectIds, setNewActionsProspectIds] = useState<string[]>([]);
    // const [removedProspectIds, setRemovedProspectIds] = useState<string[]>([]);
    const [displayLimit, setDisplayLimit] = useState(10);

    // TODO: Move inside of the card
    const { onClose: closeDetails, isOpen: isDetailsOpen } = useActionCardDetails();

    const handleRefresh = () => {
        // For conversation filters, we can just update the filter to trigger a refetch
        console.log('Refresh triggered for conversation filters');
    };

    // Set ref for action cards
    const setCardRef = (prospectId: string, ref: HTMLDivElement | null) => {
        cardRefs.current[prospectId] = ref;
    };

    const scrollToCard = (prospectId: string) => {
        const cardRef = cardRefs.current[prospectId];
        if (cardRef && feedContainerRef.current) {
            // Remove the setTimeout and use a single requestAnimationFrame
            requestAnimationFrame(() => {
                const containerRect = feedContainerRef.current?.getBoundingClientRect();
                const cardRect = cardRef.getBoundingClientRect();

                if (!containerRect) return;

                const position = cardRect.top - containerRect.top + (feedContainerRef.current?.scrollTop || 0);

                if (position > 0 && feedContainerRef.current) {
                    feedContainerRef.current.scrollTo({
                        top: position - 45,
                        behavior: 'smooth',
                    });
                }
            });
        }
    };

    // Handler for clicks on the container
    const handleContainerClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            // Only handle clicks if they're directly on the container itself
            if (e.target !== e.currentTarget) {
                return;
            }
            if (!selectedProspectId) return;

            // Check if click target is outside of the active card
            const clickedElement = e.target as HTMLElement;
            const activeCardRef = cardRefs.current[selectedProspectId];

            // click is not inside the active card
            if (!activeCardRef?.contains(clickedElement)) {
                // Check if clicked inside any other card ref
                const clickedInsideAnyCard = Object.values(cardRefs.current).some(
                    (ref) => ref && ref.contains(clickedElement)
                );

                // Only collapse if click was not inside any card
                if (!clickedInsideAnyCard) {
                    // Navigate to action feed without prospect ID to collapse
                    closeDetails();
                    const searchParams = new URLSearchParams(location.search);
                    searchParams.delete('details');
                    setTimeout(
                        () => {
                            navigate(
                                { pathname: '/new/action-feed-v3', search: searchParams.toString() },
                                { preventScrollReset: true }
                            );
                        },
                        isDetailsOpen ? 300 : 0
                    );
                }
            }
        },
        [selectedProspectId, navigate, isDetailsOpen]
    );

    // Calculate completed prospects
    const completedProspects = useMemo(() => {
        return new Set(completedProspectIds);
    }, [completedProspectIds]);

    const debouncedScrollToCard = useCallback(
        debounce((prospectId: string) => {
            scrollToCard(prospectId);
        }, 300),
        []
    );

    const handleRefreshActions = useCallback(() => {
        // Call external refresh handler if provided
        handleRefresh();

        // Create new array with new IDs at the beginning, followed by existing snapshot IDs
        // const reorderedIds = [...newActionsProspectIds, ...prospectIds.filter((id) => prospectIds.includes(id))];

        const scrollPosition = feedContainerRef.current?.scrollTop || 0;
        feedContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        // setTimeout(
        //     () => {
        //         // setSnapshotProspectIds(reorderedIds);
        //         // setNewActionsProspectIds([]);
        //         // setRemovedProspectIds([]);
        //         setDisplayLimit(10);
        //     },
        //     scrollPosition > 1000 ? 1000 : 500
        // );
    }, [completedProspects, handleRefresh]);

    useEffect(() => {
        if (selectedProspectId && cardRefs.current[selectedProspectId]) {
            debouncedScrollToCard(selectedProspectId);
        }
    }, [selectedProspectId]);

    // Handle infinite scroll
    useEffect(() => {
        const container = feedContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 400;

            if (isNearBottom && hasMoreData && !isLoadingMore) {
                loadMore();
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [hasMoreData, isLoadingMore, loadMore]);

    // TODO: Uncomment when saltfish is guaranteed to only play video once
    // useEffect(() => {
    //     console.log('OnboardingFeedFinalPage: Starting Saltfish playlist (Link Click)...');
    //     startSaltfishPlaylist('d5986487-8515-4b52-836a-ad7eed4e358b');
    //     console.log('OnboardingFeedFinalPage: Saltfish playlist started (Link Click).');
    // }, []);

    // Check if we're in initial loading state (filter applied but no data fetched yet)
    const isInitialLoading = useMemo(() => {
        return hasActiveFilter && isLoading && simpleContacts.length === 0;
    }, [hasActiveFilter, isLoading, simpleContacts.length]);

    // Enhanced filter update handlers that show loading state
    const handleSimpleFilterUpdate = useCallback(
        (filter: WeightKeys | 'urgent') => {
            setIsFilterChanging(true);
            updateFilter(filter);
            // Clear the changing state after a brief delay
            setTimeout(() => setIsFilterChanging(false), 50);
        },
        [updateFilter]
    );

    // Check if we should show loading skeletons
    const shouldShowLoading = useMemo(() => {
        // Show loading if filters are changing
        if (isFilterChanging) return true;

        // Show loading if we're in initial loading state
        if (isInitialLoading) return true;

        if (isLoading && simpleContacts.length === 0) return true;

        return false;
    }, [isFilterChanging, isInitialLoading, isLoading, simpleContacts.length]);

    const handleSettingsClick = useCallback(() => {
        setShowWeightSliders(!showWeightSliders);
    }, [showWeightSliders]);

    const handleWeightSlidersClose = useCallback(() => {
        setShowWeightSliders(false);
    }, []);

    if (!hasActiveFilter) {
        return (
            <div
                className={cn(
                    'w-full h-full overflow-y-auto flex flex-col items-center bg-background-secondary overscroll-none'
                )}
                ref={feedContainerRef}
                onMouseDown={handleContainerClick}
            >
                <FeedHeader />
                <div className="sticky top-0 w-full flex flex-row justify-between z-[100] bg-transparent px-4 pt-8 pointer-events-none overflow-visible">
                    <div className="flex items-center gap-2 pointer-events-auto">
                        {/* <CompletedActionsBadge variant="outline" className="h-8 bg-background-primary" /> */}
                    </div>
                </div>

                <div
                    className={cn(
                        'relative flex flex-col gap-6 p-4 -top-12 items-center w-[calc(min(70%,1200px))] max-w-[calc(min(70%,1200px))] h-fit',
                        'overflow-x-visible'
                    )}
                >
                    <div className="pointer-events-auto flex flex-row gap-2 items-center">
                        <SimpleMessageFilters
                            activeFilter={activeFilter as WeightKeys}
                            onFilterChange={handleSimpleFilterUpdate}
                            // onCustomFilterSubmit={handleCustomFilterSubmit}
                        />

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUseAdvancedFilters(!useAdvancedFilters)}
                            className="h-8"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            {useAdvancedFilters ? 'Simple' : 'Advanced'}
                        </Button>
                    </div>

                    {/* Weight Sliders */}
                    {showWeightSliders && (
                        <div className="pointer-events-auto absolute top-16 right-0 z-[110]">
                            <WeightSliders
                                weights={currentWeights}
                                temporalWeights={currentTemporalWeights}
                                onWeightsChange={updateCustomWeights}
                                onTemporalWeightsChange={updateCustomTemporalWeights}
                                onClose={handleWeightSlidersClose}
                            />
                        </div>
                    )}

                    <motion.div
                        className="flex flex-col items-center justify-center h-full w-full max-w-md text-center px-4 mt-20"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.4, ease: 'easeInOut' }}
                    >
                        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                            <Filter className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">No Urgent Contacts Found</h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                            There are currently no urgent contacts requiring your attention. Try selecting a different
                            category or check back later.
                        </p>
                        <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
                            <MessageSquare className="w-4 h-4" />
                            <span>Powered by AI contact classification</span>
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'w-full h-full overflow-y-auto flex flex-col items-center bg-background-secondary overscroll-none'
            )}
            ref={feedContainerRef}
            onMouseDown={handleContainerClick}
        >
            <FeedHeader />
            <div className="sticky top-0 w-full flex flex-row justify-between z-[100] bg-transparent px-4 pt-8 pointer-events-none overflow-visible">
                {/* {newActionsProspectIds.length > 0 &&
                    false && ( // TODO: Uncomment when we have a way to refresh the action feed
                        <Badge
                            variant="outline"
                            className={cn(
                                'bg-white flex items-center justify-center gap-2 pointer-events-auto cursor-pointer',
                                'animate-slide-in-from-top absolute top-10 left-1/2 -translate-x-1/2 z-[110]'
                            )}
                            onClick={handleRefreshActions}
                        >
                            <ArrowUp className="w-4 h-4" />
                            {`New action${newActionsProspectIds.length === 1 ? '' : 's'} available`}
                            <Badge variant="warning" className="p-[0.1rem] px-2">
                                {newActionsProspectIds.length}
                            </Badge>
                        </Badge>
                    )} */}
                <div className="flex items-center gap-2 pointer-events-auto">
                    {/* <CompletedActionsBadge variant="outline" className="h-8 bg-background-primary" /> */}
                </div>
            </div>

            {/* New Actions Badge */}

            <div
                className={cn(
                    'relative flex flex-col gap-6 p-4 -top-12 items-center w-[calc(min(70%,1200px))] max-w-[calc(min(70%,1200px))] h-fit',
                    'overflow-x-visible' // Allow details card to overflow and slide out
                )}
            >
                <div className="pointer-events-auto flex flex-row gap-2 items-center relative">
                    <Button variant="ghost" size="icon" className="h-8 w-8 ml-2" onClick={handleSettingsClick}>
                        <Settings className="h-4 w-4" />
                    </Button>
                    {/* Weight Sliders */}
                    {showWeightSliders && (
                        <div className="absolute top-12 left-0 z-[110]">
                            <WeightSliders
                                weights={currentWeights}
                                temporalWeights={currentTemporalWeights}
                                onWeightsChange={updateCustomWeights}
                                onTemporalWeightsChange={updateCustomTemporalWeights}
                                onClose={handleWeightSlidersClose}
                            />
                        </div>
                    )}
                    <SimpleMessageFilters
                        activeFilter={customWeights ? 'sales' : (activeFilter as WeightKeys)} // Show 'sales' as active when using custom weights
                        onFilterChange={handleSimpleFilterUpdate}
                        // onCustomFilterSubmit={handleCustomFilterSubmit}
                        // hasInvestorConversations={hasInvestorContacts}
                    />
                    {/* <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUseAdvancedFilters(!useAdvancedFilters)}
                        className="h-8"
                    >
                        <Settings className="w-4 h-4 mr-2" />
                        {useAdvancedFilters ? 'Simple' : 'Advanced'}
                    </Button> */}
                </div>
                <AnimatePresence>
                    {shouldShowLoading ? (
                        Array.from({ length: totalCount || 10 }).map((_, index) => (
                            <ActionCardSkeleton key={`skeleton-${index}`} />
                        ))
                    ) : simpleContacts?.length > 0 ? (
                        simpleContacts.map((contact, index) => (
                            // <div key={`contact-${index}`}>
                            //     {contact.fullName} ({contact.compositeScore})
                            // </div>
                            <ActionCard
                                key={contact.prospectId}
                                prospectId={contact.prospectId}
                                prospectData={{
                                    fullName: contact.fullName || contact.firstName + ' ' + contact.lastName || '',
                                    linkedinUserId: contact.linkedinUserId || '',
                                    linkedinProfileImgUrl: contact.linkedinProfileImgUrl || '',
                                    occupationTitle: contact.occupationTitle || '',
                                    organizationName: contact.organizationName || '',
                                    organizationId: contact.organizationId || '',
                                }}
                                contact={contact}
                                ref={(ref) => setCardRef(contact.prospectId, ref)}
                                isCompleted={
                                    completedProspects.has(contact.prospectId) &&
                                    !simpleContacts.some((c) => c.prospectId === contact.prospectId)
                                }
                                isExpanded={selectedProspectId === contact.prospectId}
                                overrideMotivation={contact.recommendedActions?.[0]?.reason}
                                baseUrl="/new/action-feed-v3"
                                onClick={(disableCollapse?: boolean) => {
                                    if (selectedProspectId !== contact.prospectId) {
                                        navigate({
                                            pathname: `/new/action-feed-v3/${contact.prospectId}`,
                                            search: location.search,
                                        });
                                    }

                                    if (!disableCollapse && selectedProspectId === contact.prospectId) {
                                        navigate({
                                            pathname: '/new/action-feed-v3',
                                            search: location.search,
                                        });
                                    }
                                }}
                                onRemoveCard={() => {
                                    // this should perform a mutation to update the contact with a score or user weight of -1 or something else if it's snoozed
                                    // setRemovedProspectIds((value) => [...value, contact.prospectId])
                                    markActionAsNotInterested({
                                        variables: {
                                            contactId: contact.id,
                                        },
                                        refetchQueries: [GET_CONTACTS_WEIGHTED_SCORES],
                                    });
                                }}
                            />
                        ))
                    ) : (
                        <motion.div
                            key="empty-state"
                            className="flex flex-col text-muted-foreground items-center justify-center h-full w-full"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1, duration: 0.3, ease: 'easeInOut' }}
                        >
                            <p>
                                You are all set!{' '}
                                <Link to="/new/discover/suggestions" className="text-foreground-brand-primary">
                                    Go to discover
                                </Link>{' '}
                                to find new contacts.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};
